import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Typography, Space, Upload, message, Button, Form, Row, Col, Modal, Popconfirm, notification } from 'antd';
import { BankOutlined, UserOutlined, CreditCardOutlined, EditOutlined, UploadOutlined, LoadingOutlined, InfoCircleOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from '../../../utils/axiosConfig';
import config from '../../../config';
import { useNavigate } from 'react-router-dom';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

const { Title, Text } = Typography;

const PaymentInfo = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [studentProfile, setStudentProfile] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', content: '', type: 'info' });
    const [messageApi, contextHolder] = message.useMessage();
    const [notificationApi, notificationContextHolder] = notification.useNotification();
    const [messageQueue, setMessageQueue] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPaymentInfo();
        fetchStudentProfile();
    }, []);

    // Setup Echo for real-time payment notifications
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        
        if (!token || !userStr) {
            return;
        }

        let userId;
        try {
            const user = JSON.parse(userStr);
            userId = user.id;
            if (!userId) {
                return;
            }
        } catch (e) {
            return;
        }

        // Initialize Pusher and Echo
        window.Pusher = Pusher;
        
        const echo = new Echo({
            broadcaster: 'pusher',
            key: process.env.REACT_APP_PUSHER_APP_KEY || 'your-pusher-key',
            cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER || 'mt1',
            forceTLS: true,
            encrypted: true,
            authEndpoint: `${config.apiUrl}/broadcasting/auth`,
            auth: {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
            },
        });

        // Listen to private channel for payment confirmation
        echo.private(`payment.${userId}`)
            .listen('.payment.confirmed', (data) => {
                const payload = {
                    message: 'Thanh toán thành công!',
                    description: (
                        <div>
                            <p>{data.message || 'Lệ phí dự tuyển của bạn đã được xác nhận.'}</p>
                            <p><strong>Số tiền:</strong> {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(data.amount)}</p>
                            <p><strong>Mã giao dịch:</strong> {data.transaction_id}</p>
                        </div>
                    ),
                    icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                    duration: 10,
                    placement: 'topRight',
                };

                notificationApi.success(payload);

                // Auto refresh payment info
                setTimeout(() => {
                    fetchPaymentInfo();
                }, 1000);
            });

        // Cleanup on unmount
        return () => {
            if (echo) {
                echo.leaveChannel(`payment.${userId}`);
                echo.disconnect();
            }
        };
    }, []); // Empty dependency array to run only once

    // Handle message queue
    useEffect(() => {
        if (messageQueue.length > 0) {
            const { type, content } = messageQueue[0];
            messageApi[type](content);
            setMessageQueue(prev => prev.slice(1));
        }
    }, [messageQueue, messageApi]);

    const showMessage = (content, type = 'success') => {
        setMessageQueue(prev => [...prev, { type, content }]);
    };

    const fetchPaymentInfo = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.apiUrl}/api/v1/student-payment`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setPaymentInfo(response.data.data);

                if (response.data.data?.invoice_url) {
                    setFileList([{
                        uid: '-1',
                        name: 'Invoice',
                        status: 'done',
                        url: response.data.data.attachment
                    }]);
                }
            }
        } catch (error) {
            if (error.response?.status === 401) {
                showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                navigate('/login');
            } else {
                showMessage('Không thể tải thông tin thanh toán!', 'error');
            }
        }
    };

    const fetchStudentProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.apiUrl}/api/v1/student-profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.data) {
                setStudentProfile(response.data.data);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                navigate('/login');
            }
        }
    };

    // QR code data for Vietcombank
    const qrData = {
        // accountNo: '8668858888',
        accountNo: studentProfile?.identification ? `UBXT${studentProfile.identification}` : 'UBXT',
        accountName: 'TRUONG DAI HOC CONG NGHE GTVT',
        bankName: 'BIDV',
        amount: '50000', // Amount can be dynamic if needed
        addInfo: studentProfile?.identification ? `UBXT${studentProfile.identification}` : 'UBXT'
    };

    // Format QR code data according to VietQR standard
    const qrString = `https://img.vietqr.io/image/BIDV-${qrData.accountNo}-compact2.png?amount=${qrData.amount}&addInfo=${qrData.addInfo}`;

    const showModal = (title, content, type = 'info') => {
        setModalContent({ title, content, type });
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        setIsModalVisible(false);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
    };

    const handleUpload = async (file) => {
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('invoice', file);

            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.apiUrl}/api/v1/student-payment/upload-invoice`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                showMessage('Tải lên biên lai thành công!');
                fetchPaymentInfo();
                return response.data.data;
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                navigate('/login');
            } else {
                showMessage(error.response?.data?.message || 'Không thể tải lên biên lai!', 'error');
            }
            return null;
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        name: 'invoice',
        listType: 'picture-card',
        fileList: fileList,
        maxCount: 1,
        accept: 'image/jpeg,image/png,image/gif,application/pdf',
        beforeUpload: (file) => {
            const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';
            if (!isImage && !isPDF) {
                showMessage('Chỉ được tải lên file ảnh hoặc PDF!', 'error');
                return false;
            }
            const isLt10M = file.size / 1024 / 1024 < 5;
            if (!isLt10M) {
                showMessage('Kích thước file không được vượt quá 5MB!', 'error');
                return false;
            }
            return true;
        },
        customRequest: async ({ file, onSuccess, onError }) => {
            try {
                const result = await handleUpload(file);
                if (result) {
                    onSuccess(result, file);
                } else {
                    onError(new Error('Upload failed'));
                }
            } catch (error) {
                onError(error);
            }
        },
        onChange: ({ fileList }) => {
            setFileList(fileList);
        },
        onRemove: async (file) => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.delete(
                    `${config.apiUrl}/api/v1/student-payment/remove-invoice`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                if (response.data.success) {
                    showMessage('Xóa biên lai thành công!');
                    setFileList([]);
                    fetchPaymentInfo();
                    return true;
                }
                return false;
            } catch (error) {
                if (error.response?.status === 401) {
                    showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                    navigate('/login');
                } else {
                    showMessage('Không thể xóa biên lai!', 'error');
                }
                return false;
            }
        }
    };    

    const renderContent = () => (
        <div style={{ padding: '20px' }}>
            {contextHolder}
            {notificationContextHolder}
            <Card title="Thông tin thanh toán" style={{ marginBottom: 16 }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Descriptions bordered column={1}>
                        <Descriptions.Item label={
                            <Space>
                                <UserOutlined />
                                <Text strong>Chủ tài khoản</Text>
                            </Space>
                        }>
                            <Text>TRƯỜNG ĐẠI HỌC CÔNG NGHỆ GTVT</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={
                            <Space>
                                <CreditCardOutlined />
                                <Text strong>Số tài khoản</Text>
                            </Space>
                        }>
                            <Text copyable>8668858888</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={
                            <Space>
                                <BankOutlined />
                                <Text strong>Ngân hàng</Text>
                            </Space>
                        }>
                            <Text>Ngân hàng TMCP Đầu tư và Phát triển Việt Nam (BIDV)</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={
                            <Space>
                                <EditOutlined />
                                <Text strong>Nội dung chuyển khoản</Text>
                            </Space>
                        }>
                            <Text copyable={studentProfile?.identification ? true : false}>
                                {studentProfile?.identification 
                                    ? `UBXT ${studentProfile.identification}` 
                                    : 'UBXT[Số CCCD]'}
                            </Text>
                            {!studentProfile?.identification && (
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        * Vui lòng cập nhật số CCCD trong hồ sơ để tạo mã QR tự động
                                    </Text>
                                </div>
                            )}
                        </Descriptions.Item>
                    </Descriptions>

                    <div style={{ 
                        padding: '16px', 
                        backgroundColor: paymentInfo?.is_paid && paymentInfo?.attachment ? '#f6ffed' : '#fffbe6',
                        border: `1px solid ${paymentInfo?.is_paid && paymentInfo?.attachment ? '#b7eb8f' : '#ffe58f'}`,
                        borderRadius: '4px'
                    }}>
                        <Text strong>
                            Trạng thái nộp phí đăng ký thông tin dự tuyển: {
                                paymentInfo?.is_paid 
                                    ? 'Đã hoàn thành' 
                                    : 'Chưa hoàn thành'
                            }
                        </Text>
                    </div>

                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={12}>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={5}>Quét mã QR để thanh toán</Title>
                                <div style={{ 
                                    padding: '20px', 
                                    backgroundColor: '#fff', 
                                    borderRadius: '8px',
                                    display: 'inline-block',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    <img 
                                        src={qrString}
                                        alt="QR Code Vietcombank"
                                        style={{ maxWidth: '100%', height: 'auto' }}
                                    />
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={5}>Biên lai chuyển khoản</Title>
                                {paymentInfo?.attachment ? (
                                    <div style={{ marginBottom: 16 }}>
                                        <img 
                                            src={paymentInfo?.attachment} 
                                            alt="Invoice" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: '300px', 
                                                objectFit: 'contain',
                                                marginBottom: 16
                                            }} 
                                        />
                                        <Popconfirm
                                            title="Xóa biên lai"
                                            description="Bạn có chắc chắn muốn xóa biên lai này?"
                                            onConfirm={async () => {
                                                try {
                                                    const token = localStorage.getItem('token');
                                                    const response = await axios.delete(
                                                        `${config.apiUrl}/api/v1/student-payment/remove-invoice`,
                                                        {
                                                            headers: { Authorization: `Bearer ${token}` }
                                                        }
                                                    );
                                                    if (response.data.success) {
                                                        showMessage('Xóa biên lai thành công!');
                                                        setFileList([]);
                                                        fetchPaymentInfo();
                                                    }
                                                } catch (error) {
                                                    if (error.response?.status === 401) {
                                                        showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                                                        navigate('/login');
                                                    } else {
                                                        showMessage('Không thể xóa biên lai!', 'error');
                                                    }
                                                }
                                            }}
                                            okText="Xóa"
                                            cancelText="Hủy"
                                            okButtonProps={{ danger: true }}
                                        >
                                            <Button 
                                                type="primary" 
                                                danger
                                                icon={<DeleteOutlined />}
                                            >
                                                Xóa biên lai
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                ) : (
                                    <Upload {...uploadProps} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                        <Button icon={<UploadOutlined />} loading={loading}>
                                            Tải lên
                                        </Button>
                                    </Upload>
                                )}
                            </div>
                        </Col>
                    </Row>

                </Space>
            </Card>

            <Modal
                title={modalContent.title}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="Đóng"
                cancelText="Hủy"
                okButtonProps={{
                    type: modalContent.type === 'error' ? 'primary' : 'default',
                    danger: modalContent.type === 'error'
                }}
            >
                <p>{modalContent.content}</p>
            </Modal>
        </div>
    );

    return (
        <div>
            {renderContent()}
        </div>
    );
};


export default PaymentInfo;